import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, RefreshCw, X } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/lib/AuthContext";

const ROLE_LABEL = {
  admin: "Administrator",
  internal: "Internal team",
  creator: "Creator",
  client: "Client",
};

export function PortalLayout({ nav, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.title = `${title} — AnyTribe`;
  }, [title]);

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#FCFBF9]">
      <header className="sticky top-0 z-40 border-b border-[#E6E1D8] bg-white">
        <div className="mx-auto flex max-w-[1320px] items-center justify-between gap-4 px-4 py-3.5 lg:px-8">
          <div className="flex items-center gap-5">
            <Link to="/" aria-label="AnyTribe home">
              <Logo height={26} />
            </Link>
            <span className="hidden rounded-full bg-[#F7F4EF] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#6b6660] sm:inline-block"
                  data-testid="portal-role-badge">
              {ROLE_LABEL[user?.role]}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold leading-tight" data-testid="portal-user-name">
                {user?.name}
              </p>
              <p className="text-xs body-text">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={signOut}
              data-testid="logout-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E6E1D8] px-3.5 py-2 text-sm font-semibold text-[#111111] transition-colors hover:border-[#111111]"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
            <button
              type="button"
              aria-label="Open portal menu"
              onClick={() => setOpen((v) => !v)}
              data-testid="portal-menu-toggle"
              className="rounded-full border border-[#E6E1D8] p-2 lg:hidden"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <nav className="mx-auto hidden max-w-[1320px] gap-1 px-4 lg:flex lg:px-8" data-testid="portal-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`portal-nav-${item.testId}`}
              className={({ isActive }) =>
                `-mb-px border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive
                    ? "border-[#F2811D] text-[#111111]"
                    : "border-transparent text-[#6b6660] hover:text-[#111111]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {open && (
          <nav className="border-t border-[#E6E1D8] bg-white px-4 pb-3 lg:hidden" data-testid="portal-nav-mobile">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`portal-nav-mobile-${item.testId}`}
                className={({ isActive }) =>
                  `block border-b border-[#F3EFE8] py-3.5 text-[15px] font-semibold ${
                    isActive ? "text-[#F2811D]" : "text-[#111111]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-[1320px] flex-1 px-4 py-7 lg:px-8 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, description, actions, onRefresh, refreshing }) {
  return (
    <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl md:text-3xl">{title}</h1>
        {description && (
          <p className="mt-2 max-w-3xl text-sm leading-relaxed body-text md:text-base">
            {description}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            data-testid="refresh-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#E6E1D8] bg-white px-4 py-2.5 text-sm font-semibold transition-colors hover:border-[#111111]"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
        {actions}
      </div>
    </div>
  );
}
